import { flow, map, get } from 'lodash';
import { EMPLOYMENT_TYPES } from '@src/constants';
import { idify } from '@src/util/idify';

const DEFAULT_TITLE = '';
const DEFAULT_MESSAGE = '';

function replaceInterviewQuestionName(question) {
  if (question && question.type === 'interview') {
    question.content = map(question.content, content => {
      if (content.type === 'chat-bubble') {
        return { ...content, value: replaceName(content.value) };
      }

      return content;
    });
  }

  return question;
}

export function createQuestion(question, recruiter, companyProfile) {
  return {
    key: Math.random(),
    question: replaceInterviewQuestionName(question),
    recruiter: recruiter,
    recruiterTitle: `${recruiter.title}, ${companyProfile.name}`,
  };
}

function replaceName(name) {
  return name.replace(/(:name:)/g, 'James').replace(/\n/g, '\n');
}

function checkTitle(opening) {
  if (opening && opening.title) {
    return opening;
  }

  return {
    ...opening,
    title: DEFAULT_TITLE,
  };
}

function replaceMessageName(opening) {
  if (opening && opening.message) {
    return {
      ...opening,
      message: replaceName(opening.message),
    };
  }

  return {
    ...opening,
    message: DEFAULT_MESSAGE,
  };
}

const validateOpeningData = flow([checkTitle, replaceMessageName]);

export function createOpeningDetails(opening, recruiter, companyProfile, benefits) {
  // From preview route we still fetching company profile data from old api so we need to adjust data here to display preview.
  const validatedOpening = validateOpeningData(opening);
  const isRelocation = get(validatedOpening, 'relocation.enabled');
  const positionLocations = get(validatedOpening, 'positionLocations') || [];
  const skills = get(validatedOpening, 'skills') || [];
  const supportBenefits = get(validatedOpening, 'relocationBenefits') || [];
  const headerImage = get(companyProfile, 'headerImage') || {};
  const headerImageUrl = headerImage?.fullUrl;
  const companyLogo = get(companyProfile, 'logo') || {};
  const companyLogoUrl = companyLogo?.fullUrl;
  const officeImages = (get(companyProfile, 'officeImages') || []).map(image => {
    return {
      ...image,
      fullUrl: get(image, 'fullUrl'),
    };
  });
  const locations = (get(companyProfile, 'locations') || []).map(location => {
    return {
      ...location,
      shortName: get(location, 'main'),
    };
  });
  const employmentType = get(
    Object.values(EMPLOYMENT_TYPES).find(({ key }) => key === validatedOpening.employmentType),
    'key',
  );

  return {
    /**
     * Translates old api opening data to adjust mobile preview props,
     * fills missing keys with mock data to mute some warnings coming from preview,
     * not to confuse us with EW warnings.
     * After fully migrating creation flow to gql, this logic will not be necessary anymore.
     */
    opening:
      validatedOpening && benefits
        ? {
            ...validatedOpening,
            company: {
              profile: {
                ...companyProfile,
                headerImage: {
                  ...headerImage,
                  fullUrl: headerImageUrl,
                },
                locations: locations,
                logo: {
                  ...companyLogo,
                  fullUrl: companyLogoUrl,
                },
                officeImages: officeImages,
              },
              reviewsOverview: {
                ratings: {
                  total: 0,
                },
                totalCount: 0,
              },
            },
            employmentType: employmentType || validatedOpening.employmentType,
            hiringState: 'OPEN',
            id: idify(validatedOpening),
            isRelocation: isRelocation,
            match: {},
            positionLocations: positionLocations.map(location => ({
              ...location,
              shortName: location.shortName || location.main || location.description,
            })),
            recruiter: recruiter,
            relocationBenefits: supportBenefits,
            skills: skills.map(({ skill }) => skill),
          }
        : undefined,
  };
}

export function createCompanyProfile(companyProfile) {
  return { companyProfile };
}
